// src/billing/services/use-add-remove-service.ts
// Add / remove invoice services (PROD-1286 — encounter Wrap-Up billing).
//
//   PUT /api/Billing/Invoice/{invoiceId}  body AddRemoveServiceRequestModel
//        → updated InvoiceDetail
//
// Flutter source: billing_data_source_impl.dart:932 `addRemoveService` PUTs the
// AddRemoveServiceRequestModel to `/Invoice/{invoiceId}` and parses the response
// as the updated invoice. The wrap-up screen
// (`wrap_up_billing_screen.dart:645-835`) drives just two of the model's fields:
//   - add    → addedServices:  [serviceId]   (the service-master id)
//   - remove → removeServices: [lineId]      (the invoice-line id)
// The remaining fields (include/exclude/needsApproval/auth/validity/date/
// quantity/resubmit) are claim-workbench-grade edits, out of scope for the
// wrap-up MVP — added here only as optional pass-throughs so a later surface
// can reuse the same hook without a new endpoint.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { showSuccess } from '@/shared/lib/toast-helpers';
import { invalidateBalanceViews } from '@/billing/services/invalidate-balance-views';
import type { InvoiceDetail, ResubmissionType } from '@/shared/types/billing';

/**
 * The subset of `AddRemoveServiceRequestModel` the wrap-up surface uses. Both
 * lists are arrays of ids; the backend treats an absent/empty list as "no
 * change". `addedServices` holds service-master ids; `removeServices` holds
 * invoice-line ids (`InvoiceService.id`).
 */
/**
 * Per-LINE discount — Flutter `addRemoveDiscountOnService`
 * (billing_data_source_impl.dart:1848-1858), the write behind the wrap-up row's Edit action
 * (`EditServicePopup` → `AddRemoveDiscountOnServiceEvent`).
 *
 *   PUT /api/Billing/Invoice/UpdateServiceDiscount?invoiceId={id}
 *   body { discountAmount, discountReason, invoiceDetailsId }
 *
 * DISTINCT from the invoice-wide MANAGEMENT discount (`ManagementDiscount/Create`) — that one
 * discounts the whole invoice; this one discounts a single service line.
 */
export interface UpdateServiceDiscountVars {
  /** The invoice LINE id (`invoiceDetailsId` on the wire). */
  lineId: string;
  discountAmount: number;
  discountReason: string;
}

export function useUpdateServiceDiscount(invoiceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateServiceDiscountVars) => {
      if (!invoiceId) {
        throw new Error('useUpdateServiceDiscount called without invoiceId');
      }
      return put<InvoiceDetail>(
        `${BILLING_BASE}/Invoice/UpdateServiceDiscount?invoiceId=${encodeURIComponent(invoiceId)}`,
        {
          discountAmount: vars.discountAmount,
          discountReason: vars.discountReason,
          invoiceDetailsId: vars.lineId,
        },
      );
    },
    onSuccess: () => {
      showSuccess('Discount updated');
      if (invoiceId) {
        void qc.invalidateQueries({ queryKey: ['invoice', 'detail', invoiceId] });
      }
    },
  });
}

export interface AddRemoveServiceVars {
  addedServices?: string[];
  removeServices?: string[];
  /**
   * Per-line edits — the SAME `PUT /Invoice/{id}` contract, additional arrays. Shapes are
   * Flutter's `AddRemoveServiceRequestModel` as constructed in `wrap_up_billing_screen.dart`:
   *   includedServices / excludedServices  :847-868  — the sponsored COVERAGE toggle
   *   updateAuthCode      [{id, authCode}] :880-894  — AUTH CODE
   *   updateServiceDate   [{id, serviceAddedDate}] :895-909 — SERVICE DATE
   *   updateServiceQuantity [{id, quantity}]       :910-924 — QTY
   *   updateValidity [{id, validityStartDate, validityEndDate}] :925-940 — VALIDITY PERIOD
   *     (Flutter defaults the end to the start when only a start is set)
   * Every array is omitted from the body when absent, so a single-field edit posts only that
   * field — the backend treats the others as "no change" (Flutter posts one array per action).
   */
  includedServices?: string[];
  excludedServices?: string[];
  needsApprovalServices?: string[];
  updateAuthCode?: { id: string; authCode: string }[];
  updateServiceDate?: { id: string; serviceAddedDate: string }[];
  updateServiceQuantity?: { id: string; quantity: number }[];
  updateValidity?: { id: string; validityStartDate?: string; validityEndDate?: string }[];
}

/**
 * Mutate an invoice's service lines. Returns the updated `InvoiceDetail` and
 * invalidates `['invoice','detail',invoiceId]` so the invoice + totals refetch.
 */
export function useAddRemoveService(invoiceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: AddRemoveServiceVars) => {
      if (!invoiceId) {
        throw new Error('useAddRemoveService called without invoiceId');
      }
      return put<InvoiceDetail>(`${BILLING_BASE}/Invoice/${invoiceId}`, {
        addedServices: vars.addedServices ?? [],
        removeServices: vars.removeServices ?? [],
        // Per-line edit arrays: sent ONLY when the caller supplies them. An always-present
        // empty array could read as "clear these" on a backend that distinguishes the two,
        // and add/remove (which have always shipped []) are the only two with proven
        // empty-array semantics.
        ...(vars.includedServices ? { includedServices: vars.includedServices } : {}),
        ...(vars.excludedServices ? { excludedServices: vars.excludedServices } : {}),
        ...(vars.needsApprovalServices ? { needsApprovalServices: vars.needsApprovalServices } : {}),
        ...(vars.updateAuthCode ? { updateAuthCode: vars.updateAuthCode } : {}),
        ...(vars.updateServiceDate ? { updateServiceDate: vars.updateServiceDate } : {}),
        ...(vars.updateServiceQuantity ? { updateServiceQuantity: vars.updateServiceQuantity } : {}),
        ...(vars.updateValidity ? { updateValidity: vars.updateValidity } : {}),
      });
    },
    onSuccess: (_data, vars) => {
      const added = (vars.addedServices?.length ?? 0) > 0;
      const removed = (vars.removeServices?.length ?? 0) > 0;
      showSuccess(
        removed && !added ? 'Service removed' : added ? 'Service added' : 'Invoice updated',
      );
      if (invoiceId) {
        void qc.invalidateQueries({ queryKey: ['invoice', 'detail', invoiceId] });
        // Order to Invoice: removing a line that came from an order puts that order back to
        // Pending on the server, so the wrap-up Pending orders list must re-read too.
        void qc.invalidateQueries({ queryKey: ['invoice', 'orders', invoiceId] });
      }
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
    },
  });
}

// ---------------------------------------------------------------------------
// pm-react's invoice-surface hook (PROD-3312, the appointment Billing tab).
// pm-react exports it as `useAddRemoveService()` with { invoiceId, request, resubmissionType }. The PA name
// `useAddRemoveService(invoiceId)` is the encounter wrap-up's different-shaped hook above, so pm-react's lands
// here as `useInvoiceAddRemoveService` / `InvoiceAddRemoveServiceVars`: same endpoint, body, cache write and
// invalidations as pm-react.
// ---------------------------------------------------------------------------

/**
 * One endpoint, many edit shapes — Flutter `addRemoveService` parity.
 *
 *   PUT /api/Billing/Invoice/{invoiceId}
 *   body: AddRemoveServiceRequestModel
 *
 * Mirrors Flutter `billing_data_source_impl.dart:931-944` +
 * `add_remove_service_request_model.dart`. Returns the updated
 * `InvoiceMainModel` with recalculated totals so the popup re-renders
 * with the new amounts.
 *
 * **One body shape covers five distinct edits** on the same endpoint:
 *   - Coverage change → service id in `includedServices` /
 *     `excludedServices` / `needsApprovalServices` (mutually exclusive)
 *   - Quantity change → `updateServiceQuantity: [{id, quantity}]`
 *   - Auth code change → `updateAuthCode: [{id, authCode}]`
 *   - Service-date change → `updateServiceDate: [{id, serviceAddedDate}]`
 *   - Validity-period change → `updateValidity: [{id, ...}]`
 *
 * **Field-name quirks** (verbatim from Flutter — do not rename):
 *   - `removeServices` (no `removedServices`) — Flutter spelling
 *   - `UpdateAmountToResubmit` (capital U) — only PascalCase key in the body
 *   - `updateType: 'PreClaim'` when the popup was opened from Claim
 *     Workbench (submission flow); `''` (empty) for resubmission / error
 *
 * Caller passes only the fields it's mutating. Each unused array can be
 * `[]` (the Flutter `toJson()` collapses null → `[]`). The hook
 * normalizes by stripping `undefined` from optional fields so the wire
 * stays close to Flutter's emitted shape.
 */
export interface UpdateAuthCodeEntry {
  id: string;
  authCode: string;
}

export interface UpdateServiceQuantityEntry {
  id: string;
  quantity: number;
}

export interface UpdateServiceDateEntry {
  id: string;
  serviceAddedDate: string;
}

export interface UpdateValidityEntry {
  id: string;
  validityStartDate?: string | null;
  validityEndDate?: string | null;
}

export interface AddRemoveServiceRequest {
  /** Service-master ids being added to the invoice. */
  addedServices?: string[];
  /** Per-line ids being removed from the invoice. */
  removeServices?: string[];
  /** Per-line ids whose coverage flips to Included. */
  includedServices?: string[];
  /** Per-line ids whose coverage flips to Needs Approval. */
  needsApprovalServices?: string[];
  /** Per-line ids whose coverage flips to Excluded. */
  excludedServices?: string[];
  updateAuthCode?: UpdateAuthCodeEntry[];
  updateServiceQuantity?: UpdateServiceQuantityEntry[];
  updateServiceDate?: UpdateServiceDateEntry[];
  updateValidity?: UpdateValidityEntry[];
  /** "PreClaim" when cameFrom = submission, otherwise "" (empty). */
  updateType?: string;
}

export interface InvoiceAddRemoveServiceVars {
  invoiceId: string;
  request: AddRemoveServiceRequest;
  /**
   * The `resubmissionType` slot the mounted `useInvoice` query is keyed under.
   * MUST match the value passed into `useInvoice({ resubmissionType })` at
   * the surface that owns the popup, otherwise the optimistic cache patch
   * silently writes to a different cache slot than the one the UI reads.
   * `undefined` here maps to `null` in the cache key — matching
   * `useInvoice` (`use-invoice.ts:54`: `resubmissionType ?? null`).
   */
  resubmissionType?: ResubmissionType;
}

export function useInvoiceAddRemoveService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, request }: InvoiceAddRemoveServiceVars) =>
      put<InvoiceDetail>(`${BILLING_BASE}/Invoice/${invoiceId}`, request),
    onSuccess: (data, vars) => {
      // The server response IS the updated invoice — patch the cache
      // optimistically so the totals refresh without an extra round trip.
      // The 4th slot must mirror `useInvoice`'s key (`resubmissionType ?? null`),
      // otherwise this patches a phantom cache entry and the open popup
      // sees stale totals until the predicate-based invalidate below
      // forces a refetch.
      qc.setQueryData(
        ['invoice', 'detail', vars.invoiceId, vars.resubmissionType ?? null],
        data,
      );
      // Belt-and-braces: invalidate any other resubmission-typed variants
      // for this invoice so the next open with a different `?resubmissionType=`
      // re-fetches instead of reading a stale cached snapshot.
      void qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === 'invoice' &&
          q.queryKey[1] === 'detail' &&
          q.queryKey[2] === vars.invoiceId,
      });
      // Workbench rows depend on invoice state — invalidate the list root
      // so any open page picks up the new totals / coverage state.
      void qc.invalidateQueries({ queryKey: ['claim-workbench'] });
      // Total Unpaid / Available Balance cards are keyed by patient/appointment,
      // not invoiceId — refetch them so they don't go stale after add/remove/qty
      // (PROD-1809; this is the primary invoice-edit path on the billing tab).
      invalidateBalanceViews(qc);
    },
  });
}
